import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShoppingBag, ExternalLink, Eye, MousePointerClick, Users, TrendingUp, Loader2, ArrowLeft, Globe, Share2, Award, Coins, X } from "lucide-react";
import ShareAnalyticsPanel from "@/components/ShareAnalyticsPanel";

/**
 * Creator-facing dashboard for product-link performance.
 *
 * The affiliate commission itself is handled by Guyana Discounts (the
 * tracker sits on the URL the creator pastes). This page's job is to
 * show the creator how many people are tapping through — the signal
 * they need to keep uploading shop-attached videos.
 */
export default function ShopAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeDays, setRangeDays] = useState(30);
  const [activeTab, setActiveTab] = useState("shop"); // "shop" | "sharers"

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/me/shop-analytics?range_days=${rangeDays}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, rangeDays]);

  // Sparkline peak used to scale bars.
  const peak = useMemo(() => {
    if (!data?.daily?.length) return 1;
    return Math.max(1, ...data.daily.map((d) => d.clicks));
  }, [data]);

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 text-center">
        <ShoppingBag className="h-10 w-10 text-[#FF9500] mb-4" strokeWidth={1.5} />
        <p className="font-display text-2xl font-bold mb-2">Sign in to see your shop stats</p>
        <p className="text-white/50 text-sm">Attach a guyanadiscounts.com link to any upload and track clicks here.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Link
              to="/upload"
              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white/70 mb-2"
            >
              <ArrowLeft className="h-3 w-3" /> back
            </Link>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF9500]">
              // shop.analytics
            </p>
            <h1 className="font-display font-black text-3xl mt-1">Your shop performance</h1>
            <p className="text-white/50 text-sm mt-1 max-w-xl">
              Every tap on your <span className="text-[#FF9500] font-mono">shop</span> thumbnail is tracked here. Commissions are paid directly by guyanadiscounts.com — your affiliate link tracker on the URL handles that side.
            </p>
          </div>
          <div className="flex gap-1 shrink-0">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                data-testid={`range-${d}`}
                onClick={() => setRangeDays(d)}
                className={`px-3 py-1.5 border text-[10px] font-mono uppercase tracking-wider transition-all ${
                  rangeDays === d
                    ? "border-[#FF9500] bg-[#FF9500]/10 text-[#FF9500]"
                    : "border-white/10 text-white/60 hover:border-white/25"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {/* Tab selector — routes between shop-click analytics and the new
            share/sharer analytics + creator badge awards panel. */}
        <div className="flex gap-1 border-b border-white/10">
          <button
            type="button"
            data-testid="tab-shop"
            onClick={() => setActiveTab("shop")}
            className={`px-4 py-2 text-[11px] font-mono uppercase tracking-widest transition-colors border-b-2 -mb-px ${
              activeTab === "shop"
                ? "text-[#FF9500] border-[#FF9500]"
                : "text-white/50 border-transparent hover:text-white"
            }`}
          >
            <ShoppingBag className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" strokeWidth={2} />
            Shop
          </button>
          <button
            type="button"
            data-testid="tab-sharers"
            onClick={() => setActiveTab("sharers")}
            className={`px-4 py-2 text-[11px] font-mono uppercase tracking-widest transition-colors border-b-2 -mb-px ${
              activeTab === "sharers"
                ? "text-[#39FF14] border-[#39FF14]"
                : "text-white/50 border-transparent hover:text-white"
            }`}
          >
            <Share2 className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" strokeWidth={2} />
            Sharers
          </button>
        </div>

        {activeTab === "sharers" ? (
          <ShareAnalyticsPanel rangeDays={rangeDays} />
        ) : loading ? (
          <div className="py-24 flex justify-center">
            <Loader2 className="h-6 w-6 text-[#FF9500] animate-spin" />
          </div>
        ) : !data ? (
          <p className="text-white/40 py-16 text-center">Couldn&apos;t load stats. Try refresh.</p>
        ) : data.videos.length === 0 ? (
          <div className="border border-white/10 bg-[#0A0A0C] p-8 text-center">
            <ShoppingBag className="h-8 w-8 text-[#FF9500] mx-auto mb-3" strokeWidth={1.5} />
            <p className="font-display font-bold text-lg">No shop videos yet</p>
            <p className="text-white/50 text-sm mt-1">
              Upload a video and paste a <span className="font-mono text-[#FF9500]">guyanadiscounts.com</span> product URL to start earning affiliate commissions.
            </p>
            <Link
              to="/upload"
              data-testid="empty-upload-cta"
              className="inline-block mt-4 border border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500]/10 px-5 py-2 font-mono text-xs uppercase tracking-widest"
            >
              upload a video
            </Link>
          </div>
        ) : (
          <>
            {/* Totals grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard
                icon={MousePointerClick}
                testid="stat-clicks-today"
                label="taps today"
                value={data.totals.clicks_today.toLocaleString()}
                color="#39FF14"
              />
              <StatCard
                icon={TrendingUp}
                testid="stat-clicks-7d"
                label="last 7 days"
                value={data.totals.clicks_7d.toLocaleString()}
                color="#FF9500"
              />
              <StatCard
                icon={Users}
                testid="stat-unique-viewers"
                label={`unique · ${data.totals.range_days}d`}
                value={data.totals.unique_viewers_range.toLocaleString()}
                color="#00FFFF"
              />
              <StatCard
                icon={ShoppingBag}
                testid="stat-clicks-total"
                label="all-time taps"
                value={data.totals.clicks_total.toLocaleString()}
                color="#FF007F"
              />
            </div>

            {/* Sparkline */}
            <div className="border border-white/10 bg-[#0A0A0C] p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                  // daily.taps · {data.totals.range_days}d
                </p>
                <p className="text-[10px] font-mono text-white/40">
                  peak {peak.toLocaleString()}
                </p>
              </div>
              <div className="flex items-end gap-[2px] h-32">
                {data.daily.map((d) => {
                  const h = Math.max(2, Math.round((d.clicks / peak) * 100));
                  return (
                    <div
                      key={d.day}
                      title={`${d.day}: ${d.clicks} tap${d.clicks === 1 ? "" : "s"}`}
                      className="flex-1 bg-gradient-to-t from-[#FF9500]/30 to-[#FF9500] hover:from-[#FF9500]/60 hover:to-[#FFB84D] transition-all"
                      style={{ height: `${h}%`, minHeight: "2px" }}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-[9px] font-mono text-white/30">
                <span>{data.daily[0]?.day.slice(5)}</span>
                <span>today</span>
              </div>
            </div>

            {/* AI Translator engagement — how many viewers dubbed each
                video into ES/ZH. Signal for creators that their content
                is reaching non-English audiences. */}
            {data.totals.translator_clicks && (
              <div
                data-testid="translator-panel"
                className="border border-[#39FF14]/25 bg-[#0A0A0C] p-5 shadow-[0_0_18px_rgba(57,255,20,0.08)]"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-[#39FF14]" strokeWidth={2} />
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
                      // ai.translator · all-time
                    </p>
                  </div>
                  <p className="text-[10px] font-mono text-white/40">
                    reaching non-English viewers
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="border border-white/10 p-3">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                      total translations
                    </p>
                    <p
                      className="font-display font-black text-2xl text-[#39FF14] mt-1"
                      data-testid="translator-stat-total"
                    >
                      {(data.totals.translator_clicks.total || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="border border-white/10 p-3">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex h-4 w-6 items-center justify-center rounded bg-[#39FF14]/15 text-[#39FF14] text-[9px] font-mono font-bold">
                        ES
                      </span>
                      <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                        spanish
                      </p>
                    </div>
                    <p
                      className="font-display font-black text-2xl text-white mt-1"
                      data-testid="translator-stat-es"
                    >
                      {(data.totals.translator_clicks.es || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="border border-white/10 p-3">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex h-4 w-6 items-center justify-center rounded bg-[#39FF14]/15 text-[#39FF14] text-[9px] font-mono font-bold">
                        ZH
                      </span>
                      <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                        chinese
                      </p>
                    </div>
                    <p
                      className="font-display font-black text-2xl text-white mt-1"
                      data-testid="translator-stat-zh"
                    >
                      {(data.totals.translator_clicks.zh || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="border border-white/10 p-3">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex h-4 w-6 items-center justify-center rounded bg-[#39FF14]/15 text-[#39FF14] text-[9px] font-mono font-bold">
                        EN
                      </span>
                      <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                        english
                      </p>
                    </div>
                    <p
                      className="font-display font-black text-2xl text-white mt-1"
                      data-testid="translator-stat-en"
                    >
                      {(data.totals.translator_clicks.en || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Per-video breakdown */}
            <div className="border border-white/10 bg-[#0A0A0C]">
              <div className="px-5 py-4 border-b border-white/10">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                  // per.video · {data.videos.length} shop{data.videos.length === 1 ? "" : "s"}
                </p>
                <p className="text-xs text-white/40 mt-1">
                  Ranked by all-time taps. Tap through to any video&apos;s watch page.
                </p>
              </div>
              <div className="divide-y divide-white/5">
                {data.videos.map((v, i) => (
                  <div
                    key={v.video_id}
                    data-testid={`shop-row-${i}`}
                    className="flex items-center gap-3 p-4 hover:bg-white/[0.02] transition-colors"
                  >
                    <Link
                      to={`/v/${v.video_id}`}
                      className="w-14 h-14 shrink-0 bg-black/40 overflow-hidden border border-white/10 rounded"
                    >
                      {v.product_image_url ? (
                        <img
                          src={v.product_image_url}
                          alt={v.product_title || "product"}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/30">
                          <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
                        </div>
                      )}
                    </Link>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate">
                        {v.caption || v.product_title || "Untitled video"}
                      </p>
                      <a
                        href={v.product_url}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        className="text-[10px] font-mono text-[#FF9500] flex items-center gap-1 mt-0.5 truncate hover:underline"
                      >
                        <ExternalLink className="h-3 w-3 shrink-0" />
                        <span className="truncate">{v.product_url}</span>
                      </a>
                    </div>
                    <div className="text-right shrink-0 flex gap-4">
                      <div>
                        <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">taps</p>
                        <p
                          className="font-display font-black text-lg text-[#FF9500]"
                          data-testid={`row-taps-${i}`}
                        >
                          {v.clicks_total.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">views</p>
                        <p className="font-display font-black text-lg text-white/80">
                          {v.view_count.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">ctr</p>
                        <p className="font-display font-black text-lg text-[#00FFFF]">
                          {v.ctr_pct.toFixed(1)}%
                        </p>
                      </div>
                      {v.translator_clicks && v.translator_clicks.total > 0 && (
                        <div data-testid={`row-translator-${i}`}>
                          <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 flex items-center gap-1">
                            <Globe className="h-2.5 w-2.5" strokeWidth={2.4} />
                            dubs
                          </p>
                          <p className="font-display font-black text-lg text-[#39FF14]">
                            {v.translator_clicks.total.toLocaleString()}
                          </p>
                          <p className="font-mono text-[9px] text-white/40 mt-0.5">
                            {v.translator_clicks.es || 0} ES · {v.translator_clicks.zh || 0} ZH · {v.translator_clicks.en || 0} EN
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-white/10 bg-[#0A0A0C] p-4"
      style={{ boxShadow: `inset 0 0 0 1px ${color}20` }}
    >
      <div className="flex items-center justify-between">
        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">{label}</p>
        <Icon className="h-3.5 w-3.5" style={{ color }} strokeWidth={2} />
      </div>
      <p className="font-display font-black text-3xl mt-2" style={{ color }}>
        {value}
      </p>
    </div>
  );
}
